import { atom } from 'nanostores';

import type { GlobalStyles } from '@internal/global-styles-utils';

import { z } from '@customerio/hydra-validation';

/**
 * Resume state for the brand generation wizard (CON-7724, PR-H2 part 2).
 *
 * The authoring workflow runs server-side for minutes; this store lets a user
 * leave the page (or reload, or close the tab) mid-run and pick the wizard back
 * up. It persists to `localStorage`: the workflow keeps running server-side and
 * stays queryable long after the tab is gone, and the stall clock in
 * `brand/run/brand-run-state.ts` resolves a stale draft on its own, so there is
 * no reason to throw the resume record away with the tab. Writes are
 * best-effort; a module-level `storage` listener keeps other tabs' atoms in
 * step, so a discard in one tab drops the banner in the rest. Because the record
 * is shared rather than per tab, writes are scoped to the run they are for (see
 * `updateBrandWizardDraft`) and age out (see `DRAFT_MAX_AGE_MS`). The record is
 * per browser profile, not per user, and workspace-scoped. A nanostores atom sits
 * in front so reads are synchronous and test-resettable.
 *
 * Both long halves of a run are resumable by run identity: the extract run
 * (`extractRun` + `extractSources`, phase `extracting`) and the authoring run
 * (`authorRun`, phase `authoring`). Extraction used to be excluded because it
 * ran inside a blocking modal that owned the intake sources; it now runs in the
 * background against the same status endpoint the tracker polls, so the run id
 * is all a later visit needs. `document_review` is stored as its own phase but resumes through the same
 * poll — a completed workflow stays queryable, so the status GET returns the
 * authored document again (the draft markdown is deliberately NOT persisted:
 * documents run to hundreds of KB and would crowd localStorage).
 *
 * The draft also records terminal outcomes (`authoring_failed`,
 * `sources_expired`, `stalled`) and a start time, so a run that finished or failed while
 * the user was elsewhere survives without a poll — a banner or snackbar
 * outside the wizard can read it directly instead of resuming the run.
 *
 * The draft is workspace-scoped: `getBrandWizardDraft` returns `null` on a
 * workspace mismatch so one workspace's run never resumes inside another. The
 * mismatched draft is left in place — its own workspace may still resume it.
 */

const STORAGE_KEY = 'cio-ds-brand-wizard-draft';

/**
 * Age caps on a stored draft, now that the record outlives the tab. Past the
 * workflow's 30-day retention the server cannot answer about the run at all, so
 * the record is unusable; a terminal record short-circuits the stall clock and
 * would otherwise paint the badge and resume banner indefinitely, so it expires
 * sooner. An expired record reads as no record and is dropped on the next write.
 */
const DRAFT_MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000;
const TERMINAL_DRAFT_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;

export interface BrandWizardRun {
  workflowId: string;
  runId: string;
}

export type BrandWizardDraftPhase =
  // Reading the submitted sources. Its own live phase so the tab badge, the
  // page banner and the snackbar can say so while the user works elsewhere.
  | 'extracting'
  // Extraction finished and its styles are waiting to be reviewed. Terminal to
  // the poll; the wizard re-reads the run's status when the user asks to review.
  | 'extract_ready'
  | 'extract_failed'
  | 'authoring'
  | 'document_review'
  | 'authoring_failed'
  | 'sources_expired'
  // A source could not be cut down to fit the authoring model: a fresh
  // extraction with a smaller file is the only way forward.
  | 'source_too_large'
  // The run stopped answering (404, or still in progress past the server's own
  // execution timeout). Terminal so the background poll stops; "Check again"
  // can put it back to `authoring` if the run turns out to be alive.
  | 'stalled';

const brandWizardRunSchema = z.object({
  workflowId: z.string(),
  runId: z.string(),
});

/**
 * The resumable run record, documented here since the exported type below is
 * derived from the schema (so its shape stays honest with what parses):
 *
 * - workspaceId: number
 * - phase: BrandWizardDraftPhase
 * - startedAt: epoch ms when the authoring run started (the generate POST
 *   resolved). Drafts written before the field existed have no start time;
 *   parsing defaults it to `Date.now()`.
 * - extractRun: the styles-extract run — polled while `extracting`, then kept
 *   as the run the document is authored from (and for Retry).
 * - extractSources: the sources submitted to that extract run, by the same ids
 *   the status endpoint echoes back, so a per-source progress list survives
 *   navigation. Labels only; a File's bytes are not persistable.
 * - authorRun: the authoring workflow run being polled.
 * - sourceLabels: ready source labels from the extract, saved with the
 *   document on accept.
 * - globalStyles: the reviewed-but-unsaved styles theme the document is
 *   authored against. Styles aren't persisted until the accept, so this rides
 *   the draft to survive a mid-generation reload — restored into the pending
 *   store on resume so the accept PUT persists the same tokens the document
 *   references. Small (a tokens tree), unlike the document, so it's safe in
 *   localStorage.
 * - baseGlobalStyles: the saved theme `globalStyles` was staged over, so a run
 *   entered again after the Styles page saved edits re-stages only its own
 *   changes on top of them.
 * - toastDismissed: the user closed the snackbar for this run; the run itself
 *   stays.
 */
const brandWizardDraftSchema = z.object({
  workspaceId: z.number(),
  phase: z.enum([
    'extracting',
    'extract_ready',
    'extract_failed',
    'authoring',
    'document_review',
    'authoring_failed',
    'sources_expired',
    'source_too_large',
    'stalled',
  ]),
  startedAt: z
    .number()
    .optional()
    .transform((value) => value ?? Date.now()),
  extractRun: brandWizardRunSchema.optional(),
  extractSources: z.array(z.object({ id: z.string(), label: z.string() })).optional(),
  authorRun: brandWizardRunSchema.optional(),
  sourceLabels: z.array(z.string()).optional(),
  // Stored/replayed as-is: we control every write (the reviewed theme tree), so
  // this is a typed passthrough, not a re-validation of the tokens' shape.
  globalStyles: z.custom<GlobalStyles>().optional(),
  baseGlobalStyles: z.custom<GlobalStyles>().nullable().optional(),
  toastDismissed: z.boolean().optional(),
  entry: z.enum(['empty_state', 'header', 'intro_banner', 'restart']).optional().catch(undefined),
  backgroundedAt: z.number().optional(),
  resumeCount: z.number().optional(),
  reviewedAt: z.number().optional(),
});

export type BrandWizardDraft = z.output<typeof brandWizardDraftSchema>;

const LIVE_PHASES: ReadonlySet<BrandWizardDraftPhase> = new Set(['authoring', 'extracting']);

function isExpired(draft: BrandWizardDraft, now: number): boolean {
  const cap = LIVE_PHASES.has(draft.phase) ? DRAFT_MAX_AGE_MS : TERMINAL_DRAFT_MAX_AGE_MS;
  return now - draft.startedAt > cap;
}

/** Parses a stored draft, rejecting anything that doesn't match the shape or has aged out. */
function parseDraft(raw: string): BrandWizardDraft | null {
  try {
    const parsed: unknown = JSON.parse(raw);
    const result = brandWizardDraftSchema.safeParse(parsed);
    if (!result.success) return null;
    return isExpired(result.data, Date.now()) ? null : result.data;
  } catch {
    return null;
  }
}

function readFromStorage(): BrandWizardDraft | null {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    return raw == null ? null : parseDraft(raw);
  } catch {
    // localStorage unavailable (private mode / SSR) — best-effort only.
    return null;
  }
}

export const $brandWizardDraft = atom<BrandWizardDraft | null>(readFromStorage());

// Another tab wrote the record: mirror it into this tab's atom. A null key is a
// full storage clear. Read-only by design — writing back here would let two
// tabs ping-pong a draft one of them just discarded.
if (typeof window !== 'undefined') {
  window.addEventListener('storage', (event: StorageEvent) => {
    if (event.key != null && event.key !== STORAGE_KEY) return;
    $brandWizardDraft.set(event.newValue == null ? null : parseDraft(event.newValue));
  });
}

export function setBrandWizardDraft(draft: BrandWizardDraft): void {
  $brandWizardDraft.set(draft);
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(draft));
  } catch {
    // Best-effort: the wizard still works for this pageload via the atom.
  }
}

/**
 * Drops the stored draft, but only when it belongs to the caller's workspace.
 * One shared record per browser profile means an unscoped clear would delete a
 * run another tab started in a different workspace, taking its only persisted
 * copy of the run ids and reviewed theme with it. Scoped to the workspace
 * rather than the run on purpose: cancel, a new intake and accept all mean
 * "this workspace is done with whatever it had", and a new intake deliberately
 * drops an older run whose ids it no longer holds.
 */
export function clearBrandWizardDraft(workspaceId: number): void {
  const current = $brandWizardDraft.get();
  if (current != null && current.workspaceId !== workspaceId) return;
  $brandWizardDraft.set(null);
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Best-effort only.
  }
}

/** The stored draft, or `null` when there is none or it belongs to another workspace. */
export function getBrandWizardDraft(workspaceId: number): BrandWizardDraft | null {
  const draft = $brandWizardDraft.get();
  if (draft == null || draft.workspaceId !== workspaceId) return null;
  return draft;
}

/** Identifies the record a writer means to patch, so it cannot patch a different run's. */
export interface BrandWizardDraftIdentity {
  workspaceId: number;
  /** Omitted only before a run exists; otherwise the run the caller is acting for. */
  authorRun?: BrandWizardRun;
  /** The extract run the caller is acting for, for writers that run before authoring. */
  extractRun?: BrandWizardRun;
}

function sameRun(stored: BrandWizardRun | undefined, expected: BrandWizardRun): boolean {
  return stored?.workflowId === expected.workflowId && stored.runId === expected.runId;
}

function matchesIdentity(draft: BrandWizardDraft, identity: BrandWizardDraftIdentity): boolean {
  if (draft.workspaceId !== identity.workspaceId) return false;
  if (identity.extractRun != null && !sameRun(draft.extractRun, identity.extractRun)) return false;
  if (identity.authorRun == null) return true;
  return sameRun(draft.authorRun, identity.authorRun);
}

/**
 * Merges a patch into the stored draft, but only when that draft is still the
 * run the caller is acting for. Callers poll their own run while the record is
 * shared across tabs, so without the guard one tab's poll outcome could land on
 * a run another tab had just started (and drag its reviewed theme along).
 */
export function updateBrandWizardDraft(
  patch: Partial<Omit<BrandWizardDraft, 'workspaceId'>>,
  identity: BrandWizardDraftIdentity,
): void {
  const current = $brandWizardDraft.get();
  if (current == null || !matchesIdentity(current, identity)) return;
  setBrandWizardDraft({ ...current, ...patch });
}

/** The styles fields of the draft: the staged tree and the saved tree it was staged over. */
type DraftStyles = Pick<BrandWizardDraft, 'globalStyles' | 'baseGlobalStyles'>;

/**
 * The styles a completed run records on the draft. The tree and its base are
 * only ever written together, by whoever staged the tree: the run's start and
 * every re-entry record both. A draft that already holds the pair keeps it.
 * Overwriting either half could pair this tab's tree with a base another tab's
 * re-entry recorded (or a base holding styles saved since), and the run's stale
 * copies of those styles would then read as its own changes on resume. Only a
 * draft without the pair records it now.
 */
export function stylesOnCompletion(
  draft: BrandWizardDraft | null,
  staged: GlobalStyles,
  saved: GlobalStyles | null,
): DraftStyles {
  if (draft?.globalStyles != null && draft.baseGlobalStyles !== undefined) return {};
  return { globalStyles: staged, baseGlobalStyles: saved };
}

/**
 * Test-only: re-initialise the atom from localStorage so tests can seed
 * storage and exercise the load path (the module-level read runs once per
 * import). Mirrors `__resetGlobalStylesConnectionForTests`.
 */
export function __reloadBrandWizardDraftFromStorageForTests(): void {
  $brandWizardDraft.set(readFromStorage());
}
